import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SwipeCard, { SwipeCardRef } from '../components/SwipeCard';
import { SwipeCardSkeleton } from '../components/Skeleton';
import { useSession } from '../context/SessionContext';
import { useAuth } from '../context/AuthContext';
import { swipeApi, preferencesApi, UserPreferences } from '../services/api';
import { joinSession, getSocket } from '../services/socket';
import { Restaurant } from '../types';
import { pluralize } from '../utils/pluralize';
import { PhoneIcon, GlobeIcon, CategoryPlaceholder } from '../components/Icons';
import WorkingHoursBadge from '../components/WorkingHoursBadge';
import Button from '../components/Button';
import SessionProgress from '../components/SessionProgress';
import ProfileButton from '../components/ProfileButton';
import { calcPreferenceScore } from '../components/PreferencesEditor';

const SwipePage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { participantId, restaurants, loading, loadRestaurants, removeRestaurant, addMatch, setParticipantId } = useSession();
  const [totalCount, setTotalCount] = useState(0);
  const [matchNotification, setMatchNotification] = useState<Restaurant | null>(null);
  const [swiping, setSwiping] = useState(false);
  const childRefs = useRef<Map<string, SwipeCardRef>>(new Map());
  const [userPrefs, setUserPrefs] = useState<Omit<UserPreferences, 'isConfigured'> | null>(null);

  // Restore participant ID
  useEffect(() => {
    if (!sessionId) return;
    const storedPid = localStorage.getItem(`session_${sessionId}_pid`);
    if (storedPid && !participantId) {
      setParticipantId(storedPid);
    }
  }, [sessionId, participantId, setParticipantId]);

  // Load restaurants
  useEffect(() => {
    if (!sessionId || !participantId) return;
    loadRestaurants(sessionId, participantId);
  }, [sessionId, participantId, loadRestaurants]);

  // Load user preferences for sorting
  useEffect(() => {
    if (!isAuthenticated) return;
    preferencesApi.get().then((data) => {
      const { isConfigured: _, ...rest } = data;
      setUserPrefs(rest);
    }).catch(() => {});
  }, [isAuthenticated]);

  // totalCount = максимум виденных ресторанов (только растёт, не убывает при свайпах)
  useEffect(() => {
    if (restaurants.length > totalCount) {
      setTotalCount(restaurants.length);
    }
  }, [restaurants.length, totalCount]);

  // Sort restaurants by preference score: liked → end of array (shown first), disliked → start (shown last)
  const sortedRestaurants = React.useMemo(() => {
    if (!userPrefs) return restaurants;
    return [...restaurants].sort(
      (a, b) => calcPreferenceScore(a, userPrefs) - calcPreferenceScore(b, userPrefs),
    );
  }, [restaurants, userPrefs]);

  // Socket events
  useEffect(() => {
    if (!sessionId || !participantId) return;

    joinSession(sessionId, participantId);
    const socket = getSocket();

    socket.on('match-found', ({ restaurant }: { restaurant: Restaurant }) => {
      setMatchNotification(restaurant);
      addMatch({ restaurant, matchedAt: new Date().toISOString() });
      setTimeout(() => setMatchNotification(null), 3000);
    });

    socket.on('session-completed', () => {
      navigate(`/session/${sessionId}/results`);
    });

    return () => {
      socket.off('match-found');
      socket.off('session-completed');
    };
  }, [sessionId, participantId, navigate, addMatch]);

  const sendSwipe = useCallback(async (direction: 'left' | 'right', restaurant: Restaurant): Promise<boolean> => {
    if (!sessionId || !participantId) return false;

    try {
      await swipeApi.swipe(sessionId, {
        participantId,
        restaurantId: restaurant.id,
        direction,
      });
      return true;
    } catch (err) {
      console.error('Swipe API error:', err);
      return false;
    }
  }, [sessionId, participantId]);

  const handleCardSwiped = useCallback(async (direction: string, restaurant: Restaurant) => {
    const success = await sendSwipe(direction as 'left' | 'right', restaurant);
    if (success) {
      // Удаляем карточку только при успешном API-вызове
      removeRestaurant(restaurant.id);
    } else {
      // При ошибке — восстанавливаем карточку
      const ref = childRefs.current.get(restaurant.id);
      if (ref) {
        await ref.restoreCard();
      }
    }
  }, [sendSwipe, removeRestaurant]);

  const handleButtonSwipe = async (direction: 'left' | 'right') => {
    if (restaurants.length === 0 || swiping) return;
    setSwiping(true);

    const topCard = sortedRestaurants[sortedRestaurants.length - 1];
    const ref = childRefs.current.get(topCard.id);

    if (ref) {
      await ref.swipe(direction);
    }

    // handleCardSwiped будет вызван из onSwipe коллбэка
    setTimeout(() => setSwiping(false), 600);
  };

  const swipedCount = totalCount - restaurants.length;

  const headerBar = (
    <div className="bg-white border-b border-gray-100 px-4 flex-shrink-0">
      <div className="max-w-md mx-auto flex items-center justify-between py-3">
        <button onClick={() => navigate('/')} className="text-xl font-display font-bold text-gray-900 hover:opacity-70 transition-opacity">
          Swipe<span className="text-orange-500">Eat</span>
        </button>
        <ProfileButton />
      </div>
    </div>
  );

  if (!participantId || loading) {
    return (
      <div className="h-screen overflow-hidden flex flex-col" style={{ height: '100dvh' }}>
        {headerBar}
        <div className="bg-gradient-to-br from-orange-50 to-red-50 flex-1 min-h-0 px-4 pt-3 pb-1 flex flex-col">
          <div className="max-w-md mx-auto w-full flex flex-col flex-1 min-h-0">
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" />
            <div className="relative flex-1 min-h-0">
              <div className="absolute w-full">
                <SwipeCardSkeleton />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden flex flex-col" style={{ height: '100dvh' }}>
      {headerBar}

      <div className="bg-gradient-to-br from-orange-50 to-red-50 flex-1 min-h-0 px-4 pt-3 pb-1 flex flex-col">
      <div className="max-w-md mx-auto w-full flex flex-col flex-1 min-h-0">
        <SessionProgress step={3} className="mb-2" />

        {/* Progress + counter */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-gradient-to-r from-orange-400 to-orange-600 h-2.5 rounded-full transition-all"
              style={{ width: `${totalCount > 0 ? (swipedCount / totalCount) * 100 : 0}%` }}
            />
          </div>
          <span className="text-xs text-gray-400 flex-shrink-0 tabular-nums">{swipedCount} / {totalCount}</span>
        </div>

        {/* Match notification — soft toast, no bounce */}
        {matchNotification && (
          <div className="fixed top-4 left-4 right-4 z-50 bg-white border border-orange-200 text-gray-900 p-4 rounded-2xl shadow-float text-center filter-panel-enter">
            <div className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5 text-orange-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              <p className="text-sm font-bold">Совпадение!</p>
            </div>
            <p className="text-sm text-gray-600 mt-1">{matchNotification.name}</p>
          </div>
        )}

        {/* Cards + buttons */}
        <div className="flex-1 min-h-0 flex flex-col">
          {sortedRestaurants.length > 0 ? (
            <>
              <div className="flex-1 min-h-0 relative">
              {sortedRestaurants.slice(-2).map((restaurant, i, arr) => {
              const isBack = i < arr.length - 1;
              const card = (
                <SwipeCard
                  ref={(el) => {
                    if (el) {
                      childRefs.current.set(restaurant.id, el);
                    } else {
                      childRefs.current.delete(restaurant.id);
                    }
                  }}
                  key={restaurant.id}
                  onSwipe={(dir: string) => handleCardSwiped(dir, restaurant)}
                  preventSwipe={['up', 'down']}
                  className={isBack ? 'w-full h-full' : 'relative h-full'}
                >
                <div className="bg-white rounded-card shadow-sm overflow-hidden flex flex-col h-full">
                  {/* Photo */}
                  <div className="relative h-40 sm:h-48 flex-shrink-0">
                    {restaurant.photoUrl ? (
                      <img
                        src={restaurant.photoUrl}
                        alt={restaurant.name}
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                    ) : (
                      <CategoryPlaceholder
                        cuisine={restaurant.cuisineTags || restaurant.cuisine || ''}
                        className="w-full h-full"
                        iconClassName="w-16 h-16"
                      />
                    )}
                    {/* Rating badge — top right to match catalog */}
                    <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
                      {restaurant.rating && (
                        <span className="bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                          <span className="text-yellow-500">★</span> {restaurant.rating}
                          {restaurant.reviewCount && (
                            <span className="text-gray-400 font-normal text-xs">({restaurant.reviewCount})</span>
                          )}
                        </span>
                      )}
                      {restaurant.averageCheck && (
                        <span className="bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs font-medium text-gray-600">
                          {restaurant.averageCheck}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4 overflow-y-auto flex-1 min-h-0">
                    <h3 className="text-lg font-bold text-gray-900 mb-0.5">
                      {restaurant.name}
                    </h3>
                    {restaurant.branchCount && restaurant.branchCount > 1 && (
                      <p className="text-[11px] text-gray-400 mb-1">
                        · {pluralize(restaurant.branchCount, 'филиал', 'филиала', 'филиалов')}
                      </p>
                    )}

                    {/* Cuisine tags */}
                    {restaurant.cuisineTags && (
                      <p className="text-sm text-orange-600 font-medium mb-1">
                        {restaurant.cuisineTags}
                      </p>
                    )}

                    {/* Menu tags */}
                    {restaurant.menuTags && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {restaurant.menuTags.split(', ').slice(0, 5).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded-full text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" /><circle cx="12" cy="9" r="2.5" />
                      </svg>
                      {restaurant.address}
                    </p>

                    <WorkingHoursBadge workingHours={restaurant.workingHours} />

                    {/* Phone */}
                    {restaurant.phone && (
                      <a href={`tel:${restaurant.phone}`} className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <PhoneIcon className="w-3 h-3 text-gray-400" />
                        {restaurant.phone}
                      </a>
                    )}
                    {restaurant.website && (
                      <a href={restaurant.website} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 hover:text-green-600 transition-colors">
                        <GlobeIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{restaurant.website.replace(/^https?:\/\/(www\.)?/, '')}</span>
                      </a>
                    )}

                    {/* Features — highlight known filter tags */}
                    {restaurant.features && (() => {
                      const HIGHLIGHT = ['Доставка', 'Бизнес-ланч', 'Wi-Fi', 'Детское меню', 'Завтраки', 'Можно с собакой'];
                      const present = HIGHLIGHT.filter(f => restaurant.features!.includes(f));
                      return present.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {present.map(f => (
                            <span key={f} className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-[10px] font-medium">
                              {f}
                            </span>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              </SwipeCard>
              );
              return isBack ? (
                <div key={restaurant.id} className="absolute inset-0 overflow-hidden pointer-events-none">
                  {card}
                </div>
              ) : card;
            })}
            </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Все карточки просмотрены!
              </h3>
              <p className="text-gray-500 mb-6">
                Ожидаем остальных участников...
              </p>
              <Button onClick={() => navigate(`/session/${sessionId}/results`)}>
                Смотреть результаты
              </Button>
            </div>
          )}

        {/* Swipe buttons */}
        {sortedRestaurants.length > 0 && (
          <div className="flex-shrink-0 flex justify-center gap-6 py-3">
            <button
              onClick={() => handleButtonSwipe('left')}
              disabled={swiping}
              className="w-14 h-14 rounded-full bg-white shadow-card flex items-center justify-center hover:scale-110 active:scale-90 transition-transform duration-200 disabled:opacity-50 border border-gray-100"
            >
              <svg className="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <button
              onClick={() => handleButtonSwipe('right')}
              disabled={swiping}
              className="w-14 h-14 rounded-full bg-orange-500 shadow-btn flex items-center justify-center hover:scale-110 active:scale-90 transition-transform duration-200 disabled:opacity-50"
            >
              <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </button>
          </div>
        )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default SwipePage;
